import { api } from './client';
import type { Book } from '../types/models';
import { useAuthStore } from '../store/authStore';

export type { Book };

export interface UploadProgress {
  loaded: number;
  total: number;
  percent: number;
}

export interface UploadBookParams {
  file: File;
  onProgress?: (p: UploadProgress) => void;
  signal?: AbortSignal;
}

// A successful upload returns the new book's id and starts async processing.
// On a content-hash match, the server returns the pre-existing book flagged
// as a duplicate instead of re-storing the bytes.
export type UploadBookResult =
  | { duplicate: false; id: number; status: string }
  | { duplicate: true; book: Book };

// Multipart upload via XMLHttpRequest (not fetch/api.post) for real
// upload-progress events, mirroring uploadVideo in api/videos.ts. Auth header
// is attached manually since this bypasses the shared ApiClient (JSON-only).
export async function uploadBook({
  file,
  onProgress,
  signal,
}: UploadBookParams): Promise<UploadBookResult> {
  const formData = new FormData();
  formData.append('file', file);

  const { accessToken } = useAuthStore.getState();

  return new Promise<UploadBookResult>((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('POST', '/api/books');
    xhr.withCredentials = true;
    if (accessToken) xhr.setRequestHeader('Authorization', `Bearer ${accessToken}`);

    if (signal) {
      if (signal.aborted) {
        xhr.abort();
        return;
      }
      signal.addEventListener('abort', () => xhr.abort(), { once: true });
    }

    xhr.upload.onprogress = (e) => {
      if (!onProgress || !e.lengthComputable) return;
      onProgress({
        loaded: e.loaded,
        total: e.total,
        percent: Math.round((e.loaded / e.total) * 100),
      });
    };

    xhr.onload = () => {
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new Error(xhr.responseText || `Upload failed (${xhr.status})`));
        return;
      }
      try {
        const body = JSON.parse(xhr.responseText) as
          | { id: number; status: string }
          | { duplicate: true; book: Book };
        if ('duplicate' in body && body.duplicate) {
          resolve({ duplicate: true, book: body.book });
        } else {
          const created = body as { id: number; status: string };
          resolve({ duplicate: false, id: created.id, status: created.status });
        }
      } catch {
        reject(new Error('Invalid response from server'));
      }
    };

    xhr.onerror = () => reject(new Error('Network error during upload'));
    xhr.onabort = () => reject(new Error('Upload cancelled'));

    xhr.send(formData);
  });
}

// Thrown by uploadBookFromDrive when the user's stored Google Drive grant
// can no longer be refreshed (revoked/expired) — distinct from a generic
// upload failure so the UI can prompt to reconnect Drive rather than
// treating it like any other error.
export class DriveReauthRequiredError extends Error {
  constructor() {
    super('Google Drive access needs to be reconnected');
    this.name = 'DriveReauthRequiredError';
  }
}

// Imports a book from Google Drive: the backend downloads the file using the
// signed-in user's own stored Drive credentials (never a token from the
// browser) and runs it through the same ingest pipeline as a local upload.
// Uses raw fetch rather than the shared ApiClient so the drive_reauth_required
// error body can be distinguished from a generic failure.
export async function uploadBookFromDrive(fileId: string): Promise<UploadBookResult> {
  const { accessToken } = useAuthStore.getState();

  const res = await fetch('/api/books/upload-from-drive', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
    body: JSON.stringify({ file_id: fileId }),
  });

  if (!res.ok) {
    if (res.status === 403) {
      const body = (await res.json().catch(() => null)) as { error?: string } | null;
      if (body?.error === 'drive_reauth_required') {
        throw new DriveReauthRequiredError();
      }
    }
    const text = await res.text().catch(() => '');
    throw new Error(text || `Drive import failed (${res.status})`);
  }

  const body = (await res.json()) as { id: number; status: string } | { duplicate: true; book: Book };
  if ('duplicate' in body && body.duplicate) {
    return { duplicate: true, book: body.book };
  }
  const created = body as { id: number; status: string };
  return { duplicate: false, id: created.id, status: created.status };
}

export interface GetBooksParams {
  limit?: number;
  offset?: number;
  format?: string;
  drm_status?: string;
  uploaded_by?: number;
}

export interface SearchBooksParams {
  q: string;
  limit?: number;
  offset?: number;
  uploaded_by?: number;
}

export interface UpdateBookParams {
  title?: string;
  description?: string;
  publisher?: string;
  published_date?: string;
  language?: string;
}

export async function getBooks(params?: GetBooksParams): Promise<Book[]> {
  const query = new URLSearchParams();
  if (params?.limit) query.set('limit', params.limit.toString());
  if (params?.offset) query.set('offset', params.offset.toString());
  if (params?.format) query.set('format', params.format);
  if (params?.drm_status) query.set('drm_status', params.drm_status);
  if (params?.uploaded_by) query.set('uploaded_by', params.uploaded_by.toString());

  const queryString = query.toString();
  return api.get<Book[]>(`/api/books${queryString ? `?${queryString}` : ''}`);
}

export async function searchBooks(params: SearchBooksParams): Promise<Book[]> {
  const query = new URLSearchParams();
  query.set('q', params.q);
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.offset) query.set('offset', params.offset.toString());
  if (params.uploaded_by) query.set('uploaded_by', params.uploaded_by.toString());

  return api.get<Book[]>(`/api/search?${query.toString()}`);
}

export async function semanticSearchBooks(params: SearchBooksParams): Promise<Book[]> {
  const query = new URLSearchParams();
  query.set('q', params.q);
  if (params.limit) query.set('limit', params.limit.toString());
  if (params.offset) query.set('offset', params.offset.toString());
  if (params.uploaded_by) query.set('uploaded_by', params.uploaded_by.toString());

  return api.get<Book[]>(`/api/search/semantic?${query.toString()}`);
}

export async function getBook(id: number): Promise<Book> {
  return api.get<Book>(`/api/books/${id}`);
}

export interface BookCoverToken {
  url: string;
  token: string;
  expires: string;
}

export async function getBookCoverToken(id: number): Promise<BookCoverToken> {
  return api.get<BookCoverToken>(`/api/books/${id}/cover-token`);
}

export async function updateBook(id: number, params: UpdateBookParams): Promise<Book> {
  return api.patch<Book>(`/api/books/${id}`, params);
}

export async function deleteBook(id: number): Promise<void> {
  return api.delete<void>(`/api/books/${id}`);
}

export async function reparseBook(id: number): Promise<{ book_id: number; status: string }> {
  return api.post<{ book_id: number; status: string }>(`/api/books/${id}/reparse`, {});
}
